/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import asiLogo from "@/assets/images/asi-logo.png";
import heroBackground from "@/assets/images/hero-background.jpg";
import fboServices from "@/assets/images/fbo-services.jpg";
import maintenanceServices from "@/assets/images/maintenance-services.jpg";
import propShopServices from "@/assets/images/prop-shop-services.jpg";
import charterServices from "@/assets/images/charter-services.jpg";

// Partner logos
import textronLogo from "@/assets/images/textron-logo.png";
import garminLogo from "@/assets/images/garmin-logo.jpg";
import avidyneLogo from "@/assets/images/avidyne-logo.jpg";
import mccauleyLogo from "@/assets/images/mccauley-logo.png";
import bendixkingLogo from "@/assets/images/bendixking-logo.jpg";
import nbaaLogo from "@/assets/images/nbaa-logo.jpg";
import avfuelLogo from "@/assets/images/avfuel-logo.jpg";
import l3CommunicationsLogo from "@/assets/images/l3-communications-logo.jpg";

// Louisville skyline image for Gateway section
import louisvilleSkyline from "@/assets/images/louisville-skyline.png";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // ASI Logo - multiple path variations
  "/wp-content/uploads/2020/04/ASI_Horizontal_LOGO_Rev.png": asiLogo,
  "/assets/images/asi-logo.png": asiLogo,
  "/images/asi-logo.png": asiLogo,
  "asi-logo.png": asiLogo,
  
  // Hero background image - multiple path variations
  "/wp-content/uploads/2020/06/ASI_FBO_Main-copy-scaled.jpg": heroBackground,
  "/assets/images/hero-background.jpg": heroBackground,
  "/images/hero-background.jpg": heroBackground,
  "hero-background.jpg": heroBackground,
  
  // Service gallery images - multiple path variations for each
  "/wp-content/uploads/2020/02/FBO-300x300.jpg": fboServices,
  "/assets/images/fbo-services.jpg": fboServices,
  "/images/fbo-services.jpg": fboServices,
  "fbo-services.jpg": fboServices,
  
  "/wp-content/uploads/2020/02/Maintenance-photo-300x300.jpg": maintenanceServices,
  "/assets/images/maintenance-services.jpg": maintenanceServices,
  "/images/maintenance-services.jpg": maintenanceServices,
  "maintenance-services.jpg": maintenanceServices,
  
  "/wp-content/uploads/2020/02/prop-shop-photo2-300x300.jpg": propShopServices,
  "/assets/images/prop-shop-services.jpg": propShopServices,
  "/images/prop-shop-services.jpg": propShopServices,
  "prop-shop-services.jpg": propShopServices,
  
  "/wp-content/uploads/2020/02/charter-photo3-300x300.jpg": charterServices,
  "/assets/images/charter-services.jpg": charterServices,
  "/images/charter-services.jpg": charterServices,
  "charter-services.jpg": charterServices,
  
  // Partner logos - multiple path variations for each
  "/wp-content/uploads/2020/06/Textron_Aviation_logo-1280x207.png": textronLogo,
  "/assets/images/textron-logo.png": textronLogo,
  "/images/textron-logo.png": textronLogo,
  "textron-logo.png": textronLogo,
  
  "/wp-content/uploads/2020/02/garmin-logo2-1.jpg": garminLogo,
  "/assets/images/garmin-logo.jpg": garminLogo,
  "/images/garmin-logo.jpg": garminLogo,
  "garmin-logo.jpg": garminLogo,
  
  "/wp-content/uploads/2020/02/avidyne-logo-1.jpg": avidyneLogo,
  "/assets/images/avidyne-logo.jpg": avidyneLogo,
  "/images/avidyne-logo.jpg": avidyneLogo,
  "avidyne-logo.jpg": avidyneLogo,
  
  "/wp-content/uploads/2020/06/mccauley-1-768x411-1.png": mccauleyLogo,
  "/assets/images/mccauley-logo.png": mccauleyLogo,
  "/images/mccauley-logo.png": mccauleyLogo,
  "mccauley-logo.png": mccauleyLogo,
  
  "/wp-content/uploads/2020/02/bendixking-logo.jpg": bendixkingLogo,
  "/assets/images/bendixking-logo.jpg": bendixkingLogo,
  "/images/bendixking-logo.jpg": bendixkingLogo,
  "bendixking-logo.jpg": bendixkingLogo,
  
  "/wp-content/uploads/2020/02/NBAA-logo-1.jpg": nbaaLogo,
  "/assets/images/nbaa-logo.jpg": nbaaLogo,
  "/images/nbaa-logo.jpg": nbaaLogo,
  "nbaa-logo.jpg": nbaaLogo,
  
  "/wp-content/uploads/2020/02/avfuel-logo.jpg": avfuelLogo,
  "/assets/images/avfuel-logo.jpg": avfuelLogo,
  "/images/avfuel-logo.jpg": avfuelLogo,
  "avfuel-logo.jpg": avfuelLogo,
  
  "/wp-content/uploads/2020/04/L3Communications_Logo.jpg": l3CommunicationsLogo,
  "/assets/images/l3-communications-logo.jpg": l3CommunicationsLogo,
  "/images/l3-communications-logo.jpg": l3CommunicationsLogo,
  "l3-communications-logo.jpg": l3CommunicationsLogo,
  
  // Louisville skyline - multiple path variations
  "/wp-content/uploads/2020/02/Louisville-photo-of-city-sky-line-1.jpg": louisvilleSkyline,
  "/assets/images/louisville-skyline.png": louisvilleSkyline,
  "/images/louisville-skyline.png": louisvilleSkyline,
  "louisville-skyline.png": louisvilleSkyline,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
